<template>
  <v-container fluid class="admin-mailjet-mirror">
    <NcrmHomePageHeader
      icon="mdi-email-sync-outline"
      :title="$t('admin.workflows.mailjetMirror.title')"
      :subtitle="$t('admin.workflows.mailjetMirror.subtitle')"
    >
      <v-btn color="secondary" variant="tonal" @click="$emit('back')">
        <v-icon start>mdi-arrow-left</v-icon>
        {{ $t("admin.workflows.actions.backToList") }}
      </v-btn>
      <v-btn color="primary" variant="tonal" :loading="loading" @click="loadWorkspace">
        <v-icon start>mdi-refresh</v-icon>
        {{ $t("app.actions.refresh") }}
      </v-btn>
    </NcrmHomePageHeader>

    <v-alert v-if="errorMessage" type="error" class="mt-4">{{ errorMessage }}</v-alert>
    <v-alert v-if="successMessage" type="success" class="mt-4">{{ successMessage }}</v-alert>

    <NcrmHomeKpiCards class="mt-4" :items="kpiItems" />

    <v-card variant="flat" class="admin-mailjet-mirror__panel mt-4">
      <div class="admin-mailjet-mirror__panel-header">
        <div>
          <h2>{{ form.id ? $t("admin.workflows.mailjetMirror.editTitle") : $t("admin.workflows.mailjetMirror.createTitle") }}</h2>
          <p>{{ $t("admin.workflows.mailjetMirror.formSubtitle") }}</p>
        </div>
        <v-btn color="secondary" variant="tonal" :disabled="loading || saving" @click="newMirror">
          <v-icon start>mdi-plus</v-icon>
          {{ $t("admin.workflows.actions.newMirror") }}
        </v-btn>
      </div>

      <v-skeleton-loader v-if="loading" type="article, actions" />
      <v-form v-else class="admin-mailjet-mirror__form" @submit.prevent="saveWorkflow">
        <v-row dense>
          <v-col cols="12" md="6">
            <v-select v-model="form.sourceCompanyId" :items="companyItems" item-title="name" item-value="id"
              :label="$t('admin.workflows.mailjetMirror.fields.sourceCompany')"
              hide-details="auto" />
          </v-col>
          <v-col cols="12" md="6">
            <v-select v-model="form.sourceAccountId" :items="sourceAccountItems" item-title="label" item-value="id"
              :label="$t('admin.workflows.mailjetMirror.fields.sourceAccount')"
              hide-details="auto" :disabled="!form.sourceCompanyId" />
          </v-col>
          <v-col cols="12" md="6">
            <v-select v-model="form.targetCompanyId" :items="companyItems" item-title="name" item-value="id"
              :label="$t('admin.workflows.mailjetMirror.fields.targetCompany')"
              hide-details="auto" />
          </v-col>
          <v-col cols="12" md="6">
            <v-select v-model="form.targetAccountId" :items="targetAccountItems" item-title="label" item-value="id"
              :label="$t('admin.workflows.mailjetMirror.fields.targetAccount')"
              hide-details="auto" :disabled="!form.targetCompanyId" />
          </v-col>
          <v-col cols="12" md="4">
            <v-select v-model="form.scheduleIntervalMinutes" :items="frequencyItems" item-title="label"
              item-value="value" :label="$t('admin.workflows.mailjetMirror.fields.frequency')"
              hide-details="auto" />
          </v-col>
          <v-col cols="12" md="4">
            <v-select v-model="form.status" :items="statusItems" item-title="label" item-value="value"
              :label="$t('admin.workflows.mailjetMirror.fields.status')"
              hide-details="auto" />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.name" :label="$t('admin.workflows.mailjetMirror.fields.name')"
              hide-details="auto" />
          </v-col>
        </v-row>

        <div class="admin-mailjet-mirror__actions">
          <v-btn color="primary" type="submit" :loading="saving" :disabled="!canSave">
            <v-icon start>mdi-content-save-outline</v-icon>
            {{ $t("admin.workflows.actions.save") }}
          </v-btn>
          <v-btn color="secondary" variant="tonal" :loading="running" :disabled="!canRun" @click="runWorkflow">
            <v-icon start>mdi-play-circle-outline</v-icon>
            {{ $t("admin.workflows.actions.runNow") }}
          </v-btn>
        </div>
      </v-form>
    </v-card>

    <v-card variant="flat" class="admin-mailjet-mirror__panel mt-4">
      <div class="admin-mailjet-mirror__panel-header">
        <div>
          <h2>{{ $t("admin.workflows.mailjetMirror.instancesTitle") }}</h2>
          <p>{{ $t("admin.workflows.mailjetMirror.instancesSubtitle") }}</p>
        </div>
      </div>

      <v-data-table
        :headers="headers"
        :items="workflowRows"
        :loading="loading"
        item-key="id"
        density="comfortable"
        hide-default-footer
        :items-per-page="-1"
        :row-props="workflowRowProps"
        @click:row="handleWorkflowClick"
      >
        <template #item.status="{ item }">
          <v-chip :color="statusColor(item.status)">
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>
        <template #item.action>
          <v-icon size="18" class="admin-mailjet-mirror__row-chevron">mdi-chevron-right</v-icon>
        </template>
        <template #no-data>
          <v-alert type="info" class="ma-4">
            {{ $t("admin.workflows.mailjetMirror.emptyInstances") }}
          </v-alert>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import NcrmHomeKpiCards from "@/components/ncrm/shared/NcrmHomeKpiCards.vue";
import NcrmHomePageHeader from "@/components/ncrm/shared/NcrmHomePageHeader.vue";
import adminMailjetEventMirrorLogic from "./adminMailjetEventMirrorLogic.mjs";

export default {
  name: "AdminMailjetEventMirrorWorkflow",
  components: {
    NcrmHomeKpiCards,
    NcrmHomePageHeader,
  },
  emits: ["back"],
  ...adminMailjetEventMirrorLogic,
};
</script>

<style scoped>
.admin-mailjet-mirror {
  background: var(--app-page-background);
  min-height: 100%;
}

.admin-mailjet-mirror__panel {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: var(--app-radius-base);
  padding: 20px;
}

.admin-mailjet-mirror__panel-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.admin-mailjet-mirror__panel-header h2 {
  margin: 0;
  color: var(--app-text-strong);
  font-size: 1.05rem;
  font-weight: 700;
}

.admin-mailjet-mirror__panel-header p {
  margin: 4px 0 0;
  color: var(--app-text-default);
  opacity: 0.68;
}

.admin-mailjet-mirror__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.admin-mailjet-mirror__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}

:deep(.admin-mailjet-mirror__row--active td) {
  background: rgba(var(--v-theme-primary), 0.06);
}

.admin-mailjet-mirror__row-chevron {
  opacity: 0.48;
}

@media (max-width: 720px) {
  .admin-mailjet-mirror__panel-header {
    flex-direction: column;
  }
}
</style>
